import { useMemo, useSyncExternalStore } from "react";
import {
  ALL_ACCOUNTS,
  accountMoney,
  accountMoneySources,
  balanceTotal,
  brokerFigure,
  brokerThreshold,
  chooseRoom,
  RiskStatus,
  confirmedBookAccountIds,
  TRADING_ENV,
  accountIds as tradingAccountIds,
  effectiveSelection,
  exactPositionOf,
  isWorkingOrder,
  mainAccountOf,
  markTicks,
  moneyUnavailableTitle,
  openPnlCents,
  sumCents,
  trackExcursions,
  tradingAccountOf,
  useSettings,
  useConnection,
  unconfirmedAccountIds,
  useTrading,
  type AccountSelection,
  type AccountUpdate,
  type BrokerFigure,
  type BrokerRiskInputs,
  type BrokerRiskWire,
  type BrokerVerificationDoc,
  type ExactPosition,
  type FirmFigureInputs,
  type FirmFigures,
  type FirmRules,
  type Excursion,
  type FillEvent,
  type OrderEvent,
  type RiskState,
  type SessionState,
  type TradingState,
  type MoneyAsOf,
  type MoneySources,
} from "@helm/core";
import { marketData, type InstrumentFacts } from "../md/marketData";
import { ensureVerifications, onVerificationChange, verificationDocs } from "../risk/brokerVerification";
import type { FirmRulesState } from "../risk/firmRules";
import type { DayHistoryState } from "../risk/dayHistory";
import {
  DAY_HISTORY_REFRESH_MS,
  FIRM_RULES_LOADING,
  firmRuntime,
  firmRuntimeStatus,
  loadFirmRuntime,
  onFirmChange,
  pendingHelmFigure,
} from "../risk/firmGate";
import { sameList, shallowEqual, useSlice } from "./slice";

/** Account totals use current direct Rithmic fields only. Position rows retain separate local mark estimates. */

export type AccountStatus = "unknown" | "locked";
export type AccountEnvironment = "simulation" | "live" | "demo";

export type AccountSummary = {
  id: string;
  /**
   * The Rithmic account number (e.g. APEX-500348-104). Prop firms give every account of a user
   * the same broker name, so the name never stands in for the number (owner, 2026-10-05).
   */
  label: string;
  /** Rithmic's account name, shown beside the number where there is room; null when absent. */
  brokerName: string | null;
  environment: AccountEnvironment;
  status: AccountStatus;
};

export type AccountTotals = {
  currency: string | null;
  balance: number | null;
  /** Rithmic account balance, exact cents (the Accounts table's Account Balance; Σ for All). */
  balanceCents: bigint | null;
  /** Why `balanceCents` is unknown (the Account Balance cell's tooltip); "" when known. */
  balanceReason: string;
  realizedPnl: number | null;
  unrealizedPnl: number | null;
  dayNetPnl: number | null;
  openPositions: number | null;
  workingOrders: number | null;
};

/**
 * Headline figures: the totals while "All accounts" is selected, else the selected account's.
 * For the top bar and mobile Home; per-account surfaces read `AccountView` directly.
 */
export function headlineOf(v: AccountView): AccountTotals {
  return (
    v.totals ?? {
      currency: v.currency,
      balance: v.balance,
      balanceCents: v.balanceCents,
      balanceReason: v.balanceReason,
      realizedPnl: v.realizedPnl,
      unrealizedPnl: v.unrealizedPnl,
      dayNetPnl: v.dayNetPnl,
      openPositions: v.openPositions,
      workingOrders: v.workingOrders,
    }
  );
}

export type AccountView = {
  /** Connected accounts (empty until a Rithmic login exists). */
  accounts: AccountSummary[];
  /**
   * The account per-account surfaces act on (DOM, order pad, risk chips): the selected account,
   * or the main account while `All accounts` is selected.
   */
  active: AccountSummary | null;
  /** Top-bar selection: `all`, an account id, or null without accounts. */
  selection: AccountSelection | null;
  /** True while the top bar shows every account (money chips are totals). */
  allSelected: boolean;
  /** The user's main account (setting `main_account_id`, else the first account). */
  mainId: string | null;
  /** With `All accounts` selected: totals over every account for the headline chips; else null. */
  totals: AccountTotals | null;
  currency: string | null;
  /** The trading account's figures (verified broker denomination; null = unknown). */
  balance: number | null;
  balanceCents: bigint | null;
  /** Why `balanceCents` is unknown (the Account Balance cell's tooltip); "" when known. */
  balanceReason: string;
  realizedPnl: number | null;
  unrealizedPnl: number | null;
  /** Direct Rithmic day P&L; absent remains unknown. */
  dayNetPnl: number | null;
  /** When Rithmic set the open / day P&L shown (broker ns, ADR-0056); null = not shown. */
  unrealizedAsOfNs: bigint | null;
  dayPnlAsOfNs: bigint | null;
  /**
   * Reserved broker limit fields. Unavailable until a verified broker coverage contract exists.
   */
  pdll: number | null;
  pdpt: number | null;
  dailyTrail: number | null;
  /** Reserved broker trailing floor/room; unavailable until verified. */
  trailLevelCents: number | null;
  trailRoomCents: number | null;
  /** Reserved broker trailing-arm field; unavailable until verified. */
  trailArmAtCents: number | null;
  /** Account trading-day reset, exchange time "HH:mm" (account `reset_time_ct`, default 17:00). */
  resetTimeCt: string;
  /** Reserved broker account room, $; unavailable until verified. */
  room: number | null;
  /** Firm daily-loss limit for the journal's DLL bar. */
  dailyLossLimit: number | null;
  /** Epoch ms when a Personal Lockout ends; null when not locked. */
  lockedUntil: number | null;
  openPositions: number | null;
  workingOrders: number | null;
};

export const EMPTY_ACCOUNT_VIEW: AccountView = {
  accounts: [],
  active: null,
  selection: null,
  allSelected: false,
  mainId: null,
  totals: null,
  currency: null,
  balance: null,
  balanceCents: null,
  balanceReason: "Unavailable: no Rithmic account.",
  realizedPnl: null,
  unrealizedPnl: null,
  dayNetPnl: null,
  unrealizedAsOfNs: null,
  dayPnlAsOfNs: null,
  pdll: null,
  pdpt: null,
  dailyTrail: null,
  trailLevelCents: null,
  trailRoomCents: null,
  trailArmAtCents: null,
  resetTimeCt: "17:00",
  room: null,
  dailyLossLimit: null,
  lockedUntil: null,
  openPositions: null,
  workingOrders: null,
};

// ---------------------------------------------------------------------------------------------
// Rows for the tables and the DOM

export type PositionRow = {
  key: string;
  accountId: string;
  symbol: string;
  netQty: number;
  /** Exact position (Σ price×qty); `exactCost` false when the gateway sent only a rounded avg. */
  position: ExactPosition;
  exactCost: boolean;
  info: InstrumentFacts | null;
  markTicks: number | null;
  /** null when the mark or tick value is unknown. */
  unrealizedCents: bigint | null;
  realizedCents: bigint;
};

export type AccountRow = {
  summary: AccountSummary;
  /** True only while the broker finance report is current and the account is confirmed. */
  pnlPlant: boolean;
  moneySources: MoneySources;
  financeReason: string;
  brokerCurrency: string | null;
  dayPnlCents: bigint | null;
  cashOnHandCents: bigint | null;
  closedPositionPnlCents: bigint | null;
  balanceCents: bigint | null;
  realizedCents: bigint | null;
  unrealizedCents: bigint | null;
  /** When Rithmic set each money figure shown (ADR-0056: values persist within the session). */
  asOf: MoneyAsOf;
  openPositions: number | null;
  workingOrders: number | null;
  risk: RiskState | null;
  /** Rithmic's own risk values and the figures derived only from them (ADR-0045). */
  broker: AccountBrokerRisk;
};

/** One account's Rithmic risk figures; every one carries its source and, if any, its reason. */
export type AccountBrokerRisk = {
  inputs: BrokerRiskInputs;
  /** 356 current auto-liquidation threshold, direct. */
  threshold: { valueCents: bigint | null; reason: string; raw: string | null };
  /** 356 peak balance, direct (display only; `null` when not reported or no coverage). */
  peakCents: bigint | null;
  /** Chosen in the owner's source order: Rithmic, else HELM-calculated (ADR-0045/0047). */
  trailingDrawdown: BrokerFigure;
  dailyLoss: BrokerFigure;
  /** The owner's firm rules (ADR-0047; "Firm rule · your entry"), null when none are saved. */
  firmRules: FirmRules | null;
  /**
   * HELM-calculated from those rules: every figure, with its inputs and coverage. Null while the
   * firm-rules chunk is not loaded (risk/firmGate.ts): the room figures above are then Rithmic's,
   * with the HELM-calculated second source unavailable as "loading" (or failed).
   */
  firm: FirmFigures | null;
};

/** Per account: the owner's firm rules and HELM's stored daily history (ADR-0047). */
export type FirmSources = {
  rules: ReadonlyMap<string, FirmRulesState>;
  history: ReadonlyMap<string, DayHistoryState>;
  /** HELM's figure math (the firm-rules chunk, risk/firmRuntime.ts); null until it is loaded. */
  figures: ((i: FirmFigureInputs) => FirmFigures) | null;
  /** The firm-rules chunk failed to load (its figures are unavailable, with Retry). */
  failed: boolean;
};

const NO_FIRM_SOURCES: FirmSources = { rules: new Map(), history: new Map(), figures: null, failed: false };

/** The firm-rules chunk's current sources (empty, figures null, until it is loaded). */
function currentFirmSources(): FirmSources {
  const rt = firmRuntime();
  if (rt === null) return { ...NO_FIRM_SOURCES, failed: firmRuntimeStatus() === "failed" };
  return {
    rules: rt.firmRulesStates(),
    history: rt.dayHistoryStates(),
    figures: rt.firmFigures,
    failed: false,
  };
}

export type TradingView = {
  accounts: AccountRow[];
  /** The account per-account surfaces act on (selected, or main while All is selected). */
  activeId: string | null;
  /** Top-bar selection (`all` or an account id); null without accounts. */
  selection: AccountSelection | null;
  /** Main account (setting, else first). */
  mainId: string | null;
  /** Every position with a non-zero quantity, all accounts. */
  positions: PositionRow[];
  /** Newest first. */
  orders: OrderEvent[];
  fills: readonly FillEvent[];
  session: SessionState | null;
  /**
   * Max drawdown / run-up of each open position (by `PositionRow.key`), tracked from every open
   * P&L this window computes (tables/excursion.ts). Empty from `computeTradingView` (pure).
   */
  excursions: ReadonlyMap<string, Excursion>;
};

const EMPTY_TRADING_VIEW: TradingView = {
  accounts: [],
  activeId: null,
  selection: null,
  mainId: null,
  positions: [],
  orders: [],
  fills: [],
  session: null,
  excursions: new Map(),
};

// ---------------------------------------------------------------------------------------------
// Top-bar selection (per window, remembered across reloads): "all" or one account

const ACTIVE_KEY = "helm:active-account";
let chosenSelection: AccountSelection | null = (() => {
  try {
    return localStorage.getItem(ACTIVE_KEY);
  } catch {
    return null;
  }
})();

/**
 * Select what the top bar shows: one account, or `"all"` (totals; per-account surfaces then act
 * on the main account). Viewing only: nothing is sent.
 */
export function setAccountSelection(sel: AccountSelection): void {
  chosenSelection = sel;
  try {
    localStorage.setItem(ACTIVE_KEY, sel);
  } catch {
    // Private mode: the choice lasts for this page only.
  }
  recompute();
}

/** Pick the account the top bar and DOM act on. Viewing only: nothing is sent. */
export function setActiveAccount(id: string): void {
  setAccountSelection(id);
}

// ---------------------------------------------------------------------------------------------
// Computation

function summaryOf(id: string, a: AccountUpdate | undefined, risk: RiskState | undefined): AccountSummary {
  const env: number | undefined = a?.environment;
  const locked =
    risk !== undefined &&
    (risk.status === RiskStatus.LOCKED || risk.blocksNewOrders || Number(risk.lockoutUntilMs) > Date.now());
  return {
    id,
    label: a?.rithmicAccountId || id,
    brokerName: a?.brokerAccountName || null,
    // From the gateway's own classification (ADR-0021/0026): PAPER (paper and prop-firm systems)
    // is the "demo" group, LIVE is live. An account whose frame has not arrived yet is shown as
    // simulation rather than guessed live.
    environment:
      a === undefined
        ? "simulation"
        : env === TRADING_ENV.LIVE
          ? "live"
          : env === TRADING_ENV.PAPER
            ? "demo"
            : a.isSim
              ? "simulation"
              : "live",
    status: locked ? "locked" : "unknown",
  };
}

/** Pure: trading state + market lookups → rows. Exported for tests of the view model. */
export function computeTradingView(
  s: TradingState,
  market: (
    contract: string,
  ) => { info: InstrumentFacts | null; last: number | null; bid: number | null; ask: number | null } | null,
  selectionChoice: AccountSelection | null,
  mainSetting: string | null = null,
  connected = false,
  replayReady = false,
  verifications: ReadonlyMap<string, BrokerVerificationDoc | null> = new Map(),
  firmSources: FirmSources = NO_FIRM_SOURCES,
): TradingView {
  const ids = tradingAccountIds(s);
  const positions: PositionRow[] = [];
  for (const [key, p] of s.positions) {
    if (p.netQty === 0) continue;
    const ex = exactPositionOf(p);
    const m = market(p.symbol);
    const mark = m ? markTicks(p.netQty, m.last, m.bid, m.ask) : null;
    const tv = m?.info?.tickValueCents ?? 0;
    positions.push({
      key,
      accountId: p.accountId,
      symbol: p.symbol,
      netQty: p.netQty,
      position: ex,
      exactCost: ex.exact,
      info: m?.info ?? null,
      markTicks: mark,
      unrealizedCents: mark !== null && tv > 0 ? openPnlCents(ex, mark, tv) : null,
      realizedCents: p.realizedCents,
    });
  }
  positions.sort((a, b) => (a.accountId + a.symbol).localeCompare(b.accountId + b.symbol));
  const orders = [...s.orders.values()].sort((a, b) =>
    b.createdNs > a.createdNs ? 1 : b.createdNs < a.createdNs ? -1 : 0,
  );

  const uncertain = new Set(unconfirmedAccountIds(s));
  const confirmedBooks = confirmedBookAccountIds(s, connected, replayReady);
  const accounts: AccountRow[] = ids.map((id) => {
    const a = s.accounts.get(id);
    const risk = s.risk.get(id);
    const mine = positions.filter((p) => p.accountId === id);
    // SessionState covers only the primary login. Secondary readiness is not published here,
    // and absence of an unconfirmed notice cannot certify an empty secondary book.
    const bookCurrent = confirmedBooks.has(id);
    const moneyInputs = { plant: a ?? null, connected, replayReady, unconfirmed: uncertain.has(id) };
    const money = accountMoney(moneyInputs);
    return {
      summary: summaryOf(id, a, risk),
      ...money,
      broker: brokerRiskOf(
        a,
        money,
        connected,
        replayReady,
        verifications.get(id) ?? null,
        firmSources.rules.get(id),
        firmSources.history.get(id),
        firmSources,
      ),
      moneySources: accountMoneySources(moneyInputs),
      openPositions: bookCurrent ? mine.length : null,
      workingOrders: bookCurrent
        ? orders.filter((o) => o.accountId === id && isWorkingOrder(o)).length
        : null,
      risk: risk ?? null,
    };
  });
  return {
    accounts,
    activeId: tradingAccountOf(selectionChoice, mainSetting, ids),
    selection: effectiveSelection(selectionChoice, mainSetting, ids),
    mainId: mainAccountOf(mainSetting, ids),
    positions,
    orders,
    fills: s.fills,
    session: s.session,
    excursions: new Map(),
  };
}

/**
 * Rithmic-only inputs → Rithmic figures (no HELM equity, preset, override or risk-engine value);
 * the owner's firm rules + HELM's daily history → the HELM-calculated second source (ADR-0047),
 * kept separate and used only where the Rithmic figure has no value.
 */
function brokerRiskOf(
  a: AccountUpdate | undefined,
  money: ReturnType<typeof accountMoney>,
  connected: boolean,
  replayReady: boolean,
  verification: BrokerVerificationDoc | null,
  rulesState: FirmRulesState | undefined,
  historyState: DayHistoryState | undefined,
  { figures, failed }: Pick<FirmSources, "figures" | "failed">,
): AccountBrokerRisk {
  const risk: BrokerRiskWire | null = a?.brokerRisk ?? null;
  const inputs: BrokerRiskInputs = {
    connected,
    replayReady,
    brokerCurrency: a?.brokerCurrency || null,
    financeReason: money.financeReason,
    balanceCents: money.balanceCents,
    unrealizedCents: money.unrealizedCents,
    dayPnlCents: money.dayPnlCents,
    realizedCents: money.realizedCents,
    risk,
    verification,
  };
  const threshold = brokerThreshold(inputs);
  const peakCents = threshold.reason === "" ? (risk?.autoLiqPeakBalanceCents ?? null) : null;
  if (figures === null) {
    // The firm-rules chunk is not in (yet): Rithmic's figures, the HELM second source "loading".
    return {
      inputs,
      threshold,
      peakCents,
      trailingDrawdown: chooseRoom(
        brokerFigure("trailing_drawdown", inputs),
        pendingHelmFigure("trailing_drawdown", failed),
      ),
      dailyLoss: chooseRoom(brokerFigure("daily_loss", inputs), pendingHelmFigure("daily_loss", failed)),
      firmRules: null,
      firm: null,
    };
  }
  const rules = rulesState?.rules ?? null;
  const firm = figures({
    rules,
    rulesReason:
      rulesState === undefined
        ? FIRM_RULES_LOADING
        : !rulesState.loaded
          ? rulesState.status === "error"
            ? `Your firm rules could not be loaded (${rulesState.message ?? "error"})`
            : FIRM_RULES_LOADING
          : (rulesState.message ?? ""),
    financeReason: money.financeReason,
    balanceCents: money.balanceCents,
    unrealizedCents: money.unrealizedCents,
    dayPnlCents: money.dayPnlCents,
    realizedCents: money.realizedCents,
    // The gateway's record of today is HELM's own (ADR-0047); only while the money is current.
    today: money.financeReason === "" ? (a?.dayTrack ?? null) : null,
    history: historyState?.rows ?? null,
    historyReason: historyState === undefined ? "Loading TradePrizm's daily history" : historyState.reason,
  });
  return {
    inputs,
    threshold,
    peakCents,
    // Source order (docs/RITHMIC-ACCOUNT-DATA.md): Rithmic first; HELM-calculated from the owner's
    // firm rules second (never mixed into the Rithmic figure; a difference is called out).
    trailingDrawdown: chooseRoom(brokerFigure("trailing_drawdown", inputs), firm.trailingDrawdown),
    dailyLoss: chooseRoom(brokerFigure("daily_loss", inputs), firm.dailyLoss),
    firmRules: rules,
    firm,
  };
}

const helmValueSig = (v: { valueCents: bigint | null; reason: string; label: string }): string =>
  `${v.label}/${String(v.valueCents)}/${v.reason}`;

const firmSig = (r: AccountBrokerRisk): string =>
  r.firm === null
    ? "not-loaded"
    : `${r.firmRules?.saved_at ?? ""}:${helmValueSig(r.firm.targetRoom)}:${helmValueSig(r.firm.payoutPnl)}:${r.firm.consistency.status}/${String(r.firm.consistency.ratioPct)}/${String(r.firm.consistency.maxTodayCents)}/${r.firm.consistency.reason}`;

const figureSig = (f: BrokerFigure): string =>
  `${f.source}/${String(f.valueCents)}/${String(f.verification)}/${f.reason}/${f.formula?.id ?? ""}/${f.candidates.map((c) => String(c.valueCents)).join(",")}/${f.inputs.map((i) => `${String(i.valueCents)}|${i.reason}`).join(",")}/${f.note ?? ""}/${f.alternate ? `${String(f.alternate.valueCents)}|${f.alternate.reason}` : ""}`;

const brokerSig = (r: AccountRow): string =>
  `${r.summary.id}:${r.summary.label}:${String(r.broker.threshold.valueCents)}:${r.broker.threshold.reason}:${String(r.broker.peakCents)}:${r.broker.inputs.risk?.autoLiquidateCriteria ?? ""}:${String(r.broker.inputs.risk?.rmsInfoCurrent)}:${figureSig(r.broker.trailingDrawdown)}:${figureSig(r.broker.dailyLoss)}:${firmSig(r.broker)}`;

/** Same account with the same Rithmic risk figures (for readers of one row's `broker`). */
export function sameBrokerRow(a: AccountRow | null, b: AccountRow | null): boolean {
  return a === b || (a !== null && b !== null && brokerSig(a) === brokerSig(b));
}

const dollars = (c: bigint | null): number | null => (c === null ? null : Number(c) / 100);
/** Exported for tests of the view model. */
export function accountViewOf(v: TradingView): AccountView {
  const row = v.accounts.find((a) => a.summary.id === v.activeId) ?? null;
  const base = {
    ...EMPTY_ACCOUNT_VIEW,
    accounts: v.accounts.map((a) => a.summary),
    selection: v.selection,
    allSelected: v.selection === ALL_ACCOUNTS,
    mainId: v.mainId,
  };
  if (row === null) return base;
  const r = row.risk;
  const lockUntil = r ? Number(r.lockoutUntilMs) : 0;
  const lockedUntil = lockUntil > Date.now() ? lockUntil : null;
  // All accounts: the headline figures (top bar, mobile Home) are totals; room and limits are
  // per account, so they are hidden there. The trading-account fields below stay as they are for
  // the order pad, DOM and journal, which act on one account.
  const currency = v.accounts[0]?.brokerCurrency ?? null;
  const sameCurrency = currency !== null && v.accounts.every((a) => a.brokerCurrency === currency);
  const total = (pick: (a: AccountRow) => bigint | null): number | null =>
    sameCurrency ? dollars(sumCents(v.accounts.map(pick))) : null;
  // Σ Account Balance by the Accounts Total row's rule: unknown while any account's is.
  const allBalance = balanceTotal(v.accounts);
  const totals = base.allSelected
    ? {
        currency: sameCurrency ? currency : null,
        balance: total((a) => a.balanceCents),
        balanceCents: allBalance.cents,
        balanceReason: allBalance.reason,
        realizedPnl: total((a) => a.realizedCents),
        unrealizedPnl: total((a) => a.unrealizedCents),
        dayNetPnl: total((a) => a.dayPnlCents),
        openPositions: v.accounts.every((a) => a.openPositions !== null)
          ? v.accounts.reduce((n, a) => n + (a.openPositions ?? 0), 0)
          : null,
        workingOrders: v.accounts.every((a) => a.workingOrders !== null)
          ? v.accounts.reduce((n, a) => n + (a.workingOrders ?? 0), 0)
          : null,
      }
    : null;
  return {
    ...base,
    totals,
    active: row.summary,
    currency: row.brokerCurrency,
    balance: dollars(row.balanceCents),
    balanceCents: row.balanceCents,
    balanceReason: row.balanceCents === null ? moneyUnavailableTitle(row.financeReason) : "",
    realizedPnl: dollars(row.realizedCents),
    unrealizedPnl: dollars(row.unrealizedCents),
    dayNetPnl: dollars(row.dayPnlCents),
    unrealizedAsOfNs: row.asOf.unrealized,
    dayPnlAsOfNs: row.asOf.dayPnl,
    lockedUntil,
    openPositions: row.openPositions,
    workingOrders: row.workingOrders,
  };
}

// ---------------------------------------------------------------------------------------------
// The feed: recompute on trading frames and market changes; keep open positions' contracts watched.

let tradingView: TradingView = EMPTY_TRADING_VIEW;
let accountView: AccountView = EMPTY_ACCOUNT_VIEW;
let signature = "";
const listeners = new Set<() => void>();
const watched = new Set<string>();

function marketOf(contract: string): ReturnType<Parameters<typeof computeTradingView>[1]> {
  const m = marketData.findByContract(contract);
  if (!m) return null;
  return { info: m.info, last: m.lastTicks, bid: m.bidTicks, ask: m.askTicks };
}

/** The as-of times change only with a 451 that sets a field (Rithmic sends changed fields only). */
const asOfSig = (t: MoneyAsOf): string =>
  `${String(t.balance)}/${String(t.realized)}/${String(t.unrealized)}/${String(t.dayPnl)}/${String(t.cashOnHand)}/${String(t.closedPositionPnl)}`;

function sig(v: TradingView): string {
  // Everything a view shows; cheap next to a React render of every consumer at 60 Hz.
  const pos = v.positions.map(
    (p) =>
      `${p.key}:${String(p.netQty)}:${String(p.position.openCostTicks)}:${String(p.markTicks)}:${String(p.unrealizedCents)}:${String(p.info?.tickValueCents)}:${String(p.realizedCents)}`,
  );
  const acc = v.accounts.map(
    (a) =>
      `${a.summary.id}:${a.summary.label}:${a.summary.brokerName ?? ""}:${a.summary.status}:${String(a.pnlPlant)}:${String(a.balanceCents)}:${String(a.realizedCents)}:${String(a.unrealizedCents)}:${String(a.workingOrders)}:${String(a.dayPnlCents)}:${asOfSig(a.asOf)}:${a.financeReason}:${String(a.broker.threshold.valueCents)}:${a.broker.threshold.reason}:${String(a.broker.peakCents)}:${figureSig(a.broker.trailingDrawdown)}:${figureSig(a.broker.dailyLoss)}:${firmSig(a.broker)}`,
  );
  return [
    v.activeId,
    String(v.selection),
    String(v.mainId),
    pos.join("|"),
    acc.join("|"),
    String(v.orders.length),
    String(v.fills.length),
  ].join("#");
}

function syncWatches(v: TradingView): void {
  const want = new Set(v.positions.map((p) => p.symbol));
  for (const s of want) if (!watched.has(s)) marketData.retain(s);
  for (const s of watched) if (!want.has(s)) marketData.release(s);
  watched.clear();
  for (const s of want) watched.add(s);
}

/** Same figures and account list: an equal view keeps its identity (its readers do not render). */
function sameAccountView(a: AccountView, b: AccountView): boolean {
  const { accounts: aa, active: ax, totals: at, ...ar } = a;
  const { accounts: ba, active: bx, totals: bt, ...br } = b;
  return (
    shallowEqual(ar, br) &&
    sameList(aa, ba, shallowEqual) &&
    (ax === null || bx === null ? ax === bx : shallowEqual(ax, bx)) &&
    (at === null || bt === null ? at === bt : shallowEqual(at, bt))
  );
}

let lastTrading: TradingState | null = null;
/** Accounts whose R|Trader Pro match records were asked for (ADR-0045). */
let verifiedIds = "";
/** Accounts whose firm rules were asked for (ADR-0047), once the firm-rules chunk is in. */
let firmIds = "";
/** Accounts and trading days whose daily history was asked for (ADR-0047). */
let historyKey = "";
let excursions: ReadonlyMap<string, Excursion> = new Map();

function recompute(): void {
  const s = useTrading.getState();
  const v = computeTradingView(
    s,
    marketOf,
    chosenSelection,
    useSettings.getState().settings.main_account_id,
    useConnection.getState().status === "connected",
    useConnection.getState().financeReady,
    verificationDocs(),
    currentFirmSources(),
  );
  // The owner's R|Trader Pro match records (ADR-0045) and firm rules (ADR-0047), fetched once per
  // account. Deferred: a load reports progress through the listener that recomputes this view.
  const ids = tradingAccountIds(s);
  const idKey = ids.join("\u001f");
  if (idKey !== verifiedIds) {
    verifiedIds = idKey;
    queueMicrotask(() => {
      ensureVerifications(ids);
    });
  }
  // The firm rules, HELM's daily history and the figure math are a lazy chunk (risk/firmGate.ts):
  // loaded once an account is here to compute figures for. Its arrival recomputes this view.
  const firm = firmRuntime();
  if (firm === null) {
    if (ids.length > 0)
      queueMicrotask(() => {
        loadFirmRuntime();
      });
  } else {
    if (idKey !== firmIds) {
      firmIds = idKey;
      queueMicrotask(() => {
        firm.ensureFirmRules(ids);
      });
    }
    // HELM's daily history (ADR-0047): again whenever an account's trading day moves on.
    const days = ids.map((id) => ({ id, tradingDay: s.accounts.get(id)?.dayTrack?.tradingDay || null }));
    const dayKey = days.map((d) => `${d.id}=${d.tradingDay ?? ""}`).join("\u001f");
    if (dayKey !== historyKey) {
      historyKey = dayKey;
      queueMicrotask(() => {
        firm.ensureDayHistory(days);
      });
    }
  }
  // Orders/fills tables need every frame; P&L only changes the signature.
  const key = sig(v);
  if (key === signature && s === lastTrading) return;
  signature = key;
  // The orders list keeps its identity until an OrderEvent changes it: RiskState frames and quotes
  // (many a second with a position open) then leave the Orders table and the DOM alone.
  const orders = lastTrading !== null && s.orders === lastTrading.orders ? tradingView.orders : v.orders;
  lastTrading = s;
  excursions = trackExcursions(
    excursions,
    v.positions.map((p) => ({ key: p.key, netQty: p.netQty, openPnlCents: p.unrealizedCents })),
    Date.now(),
  );
  tradingView = { ...v, orders, excursions };
  const view = accountViewOf(v);
  if (!sameAccountView(accountView, view)) accountView = view;
  syncWatches(v);
  for (const l of listeners) l();
}

let running = 0;
let stopFeed: (() => void) | null = null;

/** Start deriving the views (signed-in shell and the dev preview). Ref-counted. */
export function startAccountFeed(): () => void {
  running++;
  if (running === 1) {
    const offTrading = useTrading.subscribe(recompute);
    const offConnection = useConnection.subscribe((st, prev) => {
      if (st.status !== prev.status || st.financeReady !== prev.financeReady) {
        signature = "";
        recompute();
      }
    });
    const offMarket = marketData.onAnyChange(recompute);
    const offVerification = onVerificationChange(() => {
      signature = "";
      recompute();
    });
    // The firm-rules chunk arriving (or failing), then its rules and history loads.
    const offFirm = onFirmChange(() => {
      signature = "";
      recompute();
    });
    // The stored history is reloaded now and then even within a day (a missed write may land).
    const historyTimer = setInterval(() => {
      historyKey = "";
      recompute();
    }, DAY_HISTORY_REFRESH_MS);
    // The main account is a synced setting.
    const offSettings = useSettings.subscribe((st, prev) => {
      if (st.settings.main_account_id !== prev.settings.main_account_id) recompute();
    });
    recompute();
    stopFeed = () => {
      offTrading();
      offConnection();
      offMarket();
      offVerification();
      offFirm();
      clearInterval(historyTimer);
      offSettings();
      for (const s of watched) marketData.release(s);
      watched.clear();
    };
  }
  return () => {
    running--;
    if (running === 0) {
      stopFeed?.();
      stopFeed = null;
    }
  };
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

/**
 * The whole trading view: re-renders on every trading frame and, with a position open, on every
 * quote (P&L). Readers that show less read a slice (`useTradingViewSlice`).
 */
export function useTradingView(): TradingView {
  return useSyncExternalStore(subscribe, () => tradingView);
}

const getTradingView = (): TradingView => tradingView;

/** A slice of the trading view; the reader re-renders only when `isEqual` says it changed. */
export function useTradingViewSlice<T>(select: (v: TradingView) => T, isEqual: (a: T, b: T) => boolean): T {
  return useSlice(subscribe, getTradingView, select, isEqual);
}

/** The account list as the account pickers and table labels read it (no money). */
export type AccountList = readonly { readonly summary: AccountSummary }[];

/** Same accounts with the same id, label, environment and status. */
export function sameAccounts(a: AccountList, b: AccountList): boolean {
  return sameList(a, b, (x, y) => shallowEqual(x.summary, y.summary));
}

/** The top-bar selection and the account list, without money. */
export type SelectionSlice = { accounts: AccountList } & Pick<
  TradingView,
  "activeId" | "selection" | "mainId"
>;

export const selectionSlice = (v: TradingView): SelectionSlice => ({
  accounts: v.accounts,
  activeId: v.activeId,
  selection: v.selection,
  mainId: v.mainId,
});

export function sameSelection(a: SelectionSlice, b: SelectionSlice): boolean {
  return (
    a.activeId === b.activeId &&
    a.selection === b.selection &&
    a.mainId === b.mainId &&
    sameAccounts(a.accounts, b.accounts)
  );
}

/** The top-bar selection and account list: not re-rendered by quotes or RiskState frames. */
export function useSelectionSlice(): SelectionSlice {
  return useTradingViewSlice(selectionSlice, sameSelection);
}

function findPosition(v: TradingView, accountId: string | null, contract: string | null): PositionRow | null {
  return accountId === null || contract === null
    ? null
    : (v.positions.find((p) => p.accountId === accountId && p.symbol === contract) ?? null);
}

function samePositionRow(a: PositionRow | null, b: PositionRow | null): boolean {
  if (a === null || b === null) return a === b;
  return (
    a.key === b.key &&
    a.netQty === b.netQty &&
    a.position.openCostTicks === b.position.openCostTicks &&
    a.exactCost === b.exactCost &&
    a.info === b.info &&
    a.markTicks === b.markTicks &&
    a.unrealizedCents === b.unrealizedCents &&
    a.realizedCents === b.realizedCents
  );
}

/**
 * The open position of `accountId` in `contract` (resolved symbol, e.g. "NQZ6"), if any, with its
 * mark and open P&L: re-renders with each quote that moves them. Readers that need only the
 * position itself use `useOpenPosition`.
 */
export function usePositionOf(accountId: string | null, contract: string | null): PositionRow | null {
  return useTradingViewSlice((v) => findPosition(v, accountId, contract), samePositionRow);
}

/** A position without its mark: changes with fills only. */
export type OpenPosition = Pick<
  PositionRow,
  "key" | "accountId" | "symbol" | "netQty" | "position" | "exactCost"
>;

function openPositionOf(p: PositionRow | null): OpenPosition | null {
  return p === null
    ? null
    : {
        key: p.key,
        accountId: p.accountId,
        symbol: p.symbol,
        netQty: p.netQty,
        position: p.position,
        exactCost: p.exactCost,
      };
}

function sameOpenPosition(a: OpenPosition | null, b: OpenPosition | null): boolean {
  if (a === null || b === null) return a === b;
  return (
    a.key === b.key &&
    a.netQty === b.netQty &&
    a.position.netQty === b.position.netQty &&
    a.position.openCostTicks === b.position.openCostTicks &&
    a.exactCost === b.exactCost
  );
}

/**
 * The open position of `accountId` in `contract` without its mark (order entry, the DOM's P&L
 * column): keeps its identity — `position` included — until a fill changes it, so quotes do not
 * re-render its readers.
 */
export function useOpenPosition(accountId: string | null, contract: string | null): OpenPosition | null {
  return useTradingViewSlice((v) => openPositionOf(findPosition(v, accountId, contract)), sameOpenPosition);
}

export const getAccountView = (): AccountView => accountView;

type AccountCounts = Pick<AccountView, "openPositions" | "workingOrders" | "allSelected">;

const accountCountsOf = (v: AccountView): AccountCounts => ({
  openPositions: v.openPositions,
  workingOrders: v.workingOrders,
  allSelected: v.allSelected,
});

/** Open positions and working orders (the toolbar's badges): quotes do not re-render them. */
export function useAccountCounts(): AccountCounts {
  return useSlice(subscribe, getAccountView, accountCountsOf, shallowEqual);
}

export function useAccountView(): AccountView {
  return useSyncExternalStore(subscribe, () => accountView);
}

/** The status line's account picker and lockout control: the account list and selection, no money. */
type PickerView = Pick<
  AccountView,
  "accounts" | "active" | "selection" | "allSelected" | "mainId" | "lockedUntil"
>;

const pickerViewOf = (v: AccountView): PickerView => ({
  accounts: v.accounts,
  active: v.active,
  selection: v.selection,
  allSelected: v.allSelected,
  mainId: v.mainId,
  lockedUntil: v.lockedUntil,
});

function samePickerView(a: PickerView, b: PickerView): boolean {
  return (
    a.selection === b.selection &&
    a.allSelected === b.allSelected &&
    a.mainId === b.mainId &&
    a.lockedUntil === b.lockedUntil &&
    (a.active === b.active || (a.active !== null && b.active !== null && shallowEqual(a.active, b.active))) &&
    sameList(a.accounts, b.accounts, shallowEqual)
  );
}

/**
 * The account view without money (`AccountView` with its figures left empty): the account picker
 * and the Personal Lockout control, which quotes and P&L changes do not re-render.
 */
export function usePickerView(): AccountView {
  const s = useSlice(subscribe, getAccountView, pickerViewOf, samePickerView);
  return useMemo(() => ({ ...EMPTY_ACCOUNT_VIEW, ...s }), [s]);
}

/** The position strip's account figures: the headline (totals while All accounts is selected). */
export type StripFigures = {
  /** The Accounts table's Account Balance (Σ with All accounts, unknown while any is). */
  balanceCents: bigint | null;
  balanceReason: string;
  unrealizedPnl: number | null;
  dayNetPnl: number | null;
  /** When Rithmic set them (one account only; totals mix several accounts' times). */
  unrealizedAsOfNs: bigint | null;
  dayPnlAsOfNs: bigint | null;
  workingOrders: number | null;
  allSelected: boolean;
};

const stripFiguresOf = (v: AccountView): StripFigures => {
  const h = headlineOf(v);
  return {
    balanceCents: h.balanceCents,
    balanceReason: h.balanceReason,
    unrealizedPnl: h.unrealizedPnl,
    dayNetPnl: h.dayNetPnl,
    unrealizedAsOfNs: v.allSelected ? null : v.unrealizedAsOfNs,
    dayPnlAsOfNs: v.allSelected ? null : v.dayPnlAsOfNs,
    workingOrders: h.workingOrders,
    allSelected: v.allSelected,
  };
};

/**
 * Balance, open P&L, day P&L (with when Rithmic set them) and working orders: re-renders when one
 * of them changes, nothing else — quotes never move the as-of times.
 */
export function useStripFigures(): StripFigures {
  return useSlice(subscribe, getAccountView, stripFiguresOf, shallowEqual);
}
